import { useCallback, useEffect, useMemo, useState } from "react";
import { useHealthRecord, useProHealthRecords, useTeamHealthRecords } from "../../../../store";
import { useQueries } from "../../../providers/Dependencies";
import { ImportDestinationOption } from "../evcImportDestinations";

export function useExistingHealthRecordTarget({
  isActive,
  targetMode,
  selectedDestination,
}: {
  isActive: boolean;
  targetMode: "new" | "existing";
  selectedDestination: ImportDestinationOption | null;
}) {
  const queries = useQueries();
  const selectedDestinationIsTeam = !!(selectedDestination?.organizationId && selectedDestination.teamId);
  const proHealthRecords = useProHealthRecords();
  const { healthRecords: teamHealthRecords } = useTeamHealthRecords(selectedDestination?.teamId ?? null);
  const [healthRecordSearchValue, setHealthRecordSearchValue] = useState("");
  const [healthRecordOptionsLoading, setHealthRecordOptionsLoading] = useState(false);
  const [healthRecordOptionsError, setHealthRecordOptionsError] = useState(false);
  const [selectedExistingHealthRecordId, setSelectedExistingHealthRecordId] = useState<string | null>(null);
  const [selectedHealthRecordLoading, setSelectedHealthRecordLoading] = useState(false);
  const [selectedHealthRecordRequiredError, setSelectedHealthRecordRequiredError] = useState(false);
  const [selectedHealthRecordLoadError, setSelectedHealthRecordLoadError] = useState(false);
  const selectedExistingHealthRecord = useHealthRecord(selectedExistingHealthRecordId ?? "");

  const healthRecordOptions = useMemo(
    () => (selectedDestinationIsTeam ? teamHealthRecords : proHealthRecords) ?? [],
    [proHealthRecords, selectedDestinationIsTeam, teamHealthRecords],
  );

  const clearSelectedHealthRecordErrors = useCallback(() => {
    setSelectedHealthRecordRequiredError(false);
    setSelectedHealthRecordLoadError(false);
  }, []);

  const markSelectedHealthRecordRequired = useCallback(() => {
    setSelectedHealthRecordRequiredError(true);
  }, []);

  useEffect(() => {
    setSelectedExistingHealthRecordId(null);
    setHealthRecordSearchValue("");
    setHealthRecordOptionsError(false);
    setHealthRecordOptionsLoading(false);
    setSelectedHealthRecordLoading(false);
    clearSelectedHealthRecordErrors();
  }, [clearSelectedHealthRecordErrors, selectedDestination?.id]);

  useEffect(() => {
    if (!isActive || targetMode !== "existing" || !selectedDestination) {
      return;
    }

    let cancelled = false;
    const searchValue = selectedDestinationIsTeam ? healthRecordSearchValue : "";

    (async () => {
      setHealthRecordOptionsLoading(true);
      setHealthRecordOptionsError(false);
      try {
        if (selectedDestination.organizationId && selectedDestination.teamId) {
          await queries.teamHealthRecordsQuery.call(
            selectedDestination.organizationId,
            selectedDestination.teamId,
            searchValue.trim() || undefined,
          );
        } else {
          await queries.proAllHealthRecordsQuery.call();
        }
      } catch {
        if (!cancelled) {
          setHealthRecordOptionsError(true);
        }
      } finally {
        if (!cancelled) {
          setHealthRecordOptionsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [healthRecordSearchValue, isActive, queries, selectedDestination, selectedDestinationIsTeam, targetMode]);

  useEffect(() => {
    if (!isActive || targetMode !== "existing" || !selectedDestination || !selectedExistingHealthRecordId) {
      return;
    }

    let cancelled = false;
    (async () => {
      setSelectedHealthRecordLoading(true);
      setSelectedHealthRecordLoadError(false);
      try {
        if (selectedDestination.organizationId && selectedDestination.teamId) {
          await queries.teamHealthRecordQuery.call(
            selectedDestination.organizationId,
            selectedDestination.teamId,
            selectedExistingHealthRecordId,
          );
        } else {
          await queries.proHealthRecordQuery.call(selectedExistingHealthRecordId, null);
        }
      } catch {
        if (!cancelled) {
          setSelectedHealthRecordLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setSelectedHealthRecordLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive, queries, selectedDestination, selectedExistingHealthRecordId, targetMode]);

  return {
    healthRecordOptions,
    healthRecordOptionsLoading,
    healthRecordOptionsError,
    selectedExistingHealthRecord,
    selectedExistingHealthRecordId,
    setSelectedExistingHealthRecordId,
    selectedHealthRecordLoading,
    selectedHealthRecordRequiredError,
    selectedHealthRecordLoadError,
    clearSelectedHealthRecordErrors,
    markSelectedHealthRecordRequired,
    setHealthRecordSearchValue,
  };
}
